import React from "react";
import { Sparkles, MessageCircle, Stethoscope, Award } from "lucide-react";

/**
 * Healthcare-flavored welcome panel for the public chatbot.
 *
 * Consultative Experience v1 — empathy-first copy + credential chips.
 *
 * Reads tenant data already present in the public meta payload:
 *   - company.name, chatbot.assistant_name, chatbot.welcome_message
 *   - chatbot.consultative_intro (empathy paragraph)
 *   - chatbot.credentials_strip (trust pill row)
 *   - suggested_prompts (already merged with chatbot.custom_starter_prompts)
 *
 * Reusable for any future healthcare tenant — copy & icons are
 * vertical-generic.
 */
export default function HealthcareHero({
  companyName,
  assistantName,
  welcomeMessage,
  consultativeIntro,
  credentialsStrip,
  suggestedPrompts,
  onAsk,
  uiBusy,
}) {
  const name = assistantName || "your AI assistant";
  const headline = welcomeMessage && welcomeMessage.trim()
    ? welcomeMessage.trim()
    : `Welcome to ${companyName || "our practice"}.`;
  const prompts = Array.isArray(suggestedPrompts) ? suggestedPrompts : [];
  const creds = Array.isArray(credentialsStrip) ? credentialsStrip : [];
  const intro = (consultativeIntro || "").trim();

  return (
    <div className="rch-hc-hero" data-testid="rch-hc-hero">
      <div className="rch-hc-hero-badge">
        <Stethoscope className="w-3.5 h-3.5" aria-hidden />
        <span>Healthcare AI Sales Engineer</span>
      </div>
      <h2 className="rch-hc-hero-h" data-testid="rch-hc-hero-title">{headline}</h2>
      <p className="rch-hc-hero-sub">
        I'm <strong>{name}</strong> — your AI guide. Ask me about EMR, billing, AI scribe, integrations, or implementation. I'm here to help you find the right fit.
      </p>
      {intro && (
        <p className="rch-hc-hero-intro" data-testid="rch-hc-hero-intro">
          {intro}
        </p>
      )}

      {creds.length > 0 && (
        <div className="rch-hc-cred-row" data-testid="rch-hc-cred-row">
          {creds.map((c) => (
            <span key={c} className="rch-hc-cred-pill">
              <Award className="w-3 h-3" aria-hidden /> {c}
            </span>
          ))}
        </div>
      )}

      {prompts.length > 0 && (
        <div className="rch-hc-quick-section">
          <div className="rch-hc-quick-label">
            <MessageCircle className="w-3.5 h-3.5" aria-hidden />
            <span>Quick questions to get started</span>
          </div>
          <div className="rch-hc-quick-grid" data-testid="rch-hc-quick-grid">
            {prompts.map(q => (
              <button
                key={q}
                type="button"
                className="rch-hc-quick-card"
                disabled={uiBusy}
                onClick={() => { if (!uiBusy) onAsk?.(q); }}
                data-testid={`rch-pill-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                style={uiBusy ? { opacity: 0.55, pointerEvents: "none" } : undefined}
              >
                <span className="rch-hc-quick-text">{q}</span>
                <Sparkles className="w-3.5 h-3.5 rch-hc-quick-spark" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
